<template>
  <AuthCard
    title="Sign in to your rewards programme"
    subtitle="Manage your rewards programme, customers and insights."
  >
    <v-form ref="formRef" v-model="formValid" @submit.prevent="onSubmit">
      <v-text-field
        v-model="email"
        label="Email"
        type="email"
        autocomplete="email"
        :rules="[rules.required, rules.email]"
        :disabled="loading"
        variant="outlined"
        density="comfortable"
        class="mb-2"
        @input="onEmailInput"
      />

      <v-text-field
        v-model="password"
        label="Password"
        :type="showPassword ? 'text' : 'password'"
        autocomplete="current-password"
        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append-inner="showPassword = !showPassword"
        :rules="[rules.required]"
        :disabled="loading"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      />

      <div class="forgot-row">
        <button
          type="button"
          class="forgot-link"
          :disabled="resetSending"
          @click="onForgotPassword"
        >
          Forgot password?
        </button>
      </div>

      <v-alert
        v-if="infoMessage"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ infoMessage }}
      </v-alert>

      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ errorMessage }}
      </v-alert>

      <v-alert
        v-if="resetMessage"
        type="success"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ resetMessage }}
      </v-alert>

      <v-btn
        type="submit"
        block
        size="large"
        :loading="loading"
        :disabled="loading"
        class="submit-btn"
      >
        Sign in
      </v-btn>
    </v-form>

    <template #footer>
      New to Lulu?
      <router-link to="/business-signup">Create a business account</router-link>
    </template>
  </AuthCard>
</template>

<script>
import { ref, reactive } from "vue";
import { useRouter, useRoute } from "vue-router";
import AuthCard from "@/components/Molecules/AuthCard.vue";
import {
  signIn,
  sendOwnerResetEmail,
  refreshEmployers,
  authStore,
} from "@/stores/authStore.js";
import { track } from "@/analytics.js";

export default {
  name: "BusinessSignInComponent",
  components: { AuthCard },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const formRef = ref(null);
    const formValid = ref(false);
    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const loading = ref(false);
    const resetSending = ref(false);
    const errorMessage = ref("");
    const resetMessage = ref("");
    const infoMessage = ref("");

    const rules = reactive({
      required: (v) => !!v || "This field is required",
      email: (v) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(v) ||
        "E-mail must be valid",
    });

    const onEmailInput = () => {
      email.value = email.value.replace(/[^a-zA-Z0-9._%+\-@]/g, "").toLowerCase();
    };

    const isSafeRedirect = (value) =>
      typeof value === "string" &&
      value.startsWith("/") &&
      !value.startsWith("//");

    const onSubmit = async () => {
      errorMessage.value = "";
      resetMessage.value = "";
      infoMessage.value = "";
      const validation = await formRef.value?.validate();
      if (!validation?.valid) return;

      loading.value = true;
      try {
        await signIn(email.value, password.value);
        track("business_signin_succeeded", {});

        const redirect = route.query.redirect;
        if (isSafeRedirect(redirect)) {
          router.push(redirect);
          return;
        }

        await refreshEmployers();
        const ids = authStore.employerBusinessIds || [];
        if (ids.length === 0) {
          router.push("/register-business");
        } else {
          router.push("/dashboard/insights");
        }
      } catch (error) {
        errorMessage.value =
          error?.message || "Sign in failed. Please try again.";
        track("business_signin_failed", { error_message: errorMessage.value });
      } finally {
        loading.value = false;
      }
    };

    const onForgotPassword = async () => {
      errorMessage.value = "";
      resetMessage.value = "";
      infoMessage.value = "";
      if (!email.value || rules.email(email.value) !== true) {
        infoMessage.value = "Enter your email first to receive a reset link.";
        return;
      }
      resetSending.value = true;
      const neutralConfirmation = `If an account exists for ${email.value}, we've sent a reset link.`;
      try {
        await sendOwnerResetEmail(email.value);
        resetMessage.value = neutralConfirmation;
        track("business_password_reset_requested", {});
      } catch (error) {
        // Treat "no account" as success silently to avoid email enumeration.
        if (error?.code === "auth/user-not-found") {
          resetMessage.value = neutralConfirmation;
          track("business_password_reset_requested", {});
        } else {
          errorMessage.value =
            error?.message || "Could not send a reset email. Please try again.";
        }
      } finally {
        resetSending.value = false;
      }
    };

    return {
      formRef,
      formValid,
      email,
      password,
      showPassword,
      loading,
      resetSending,
      errorMessage,
      resetMessage,
      infoMessage,
      rules,
      onEmailInput,
      onSubmit,
      onForgotPassword,
    };
  },
};
</script>

<style scoped>
.submit-btn {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.submit-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.forgot-row {
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 14px;
}

.forgot-link {
  background: none;
  border: none;
  padding: 4px 2px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(0, 109, 62);
  cursor: pointer;
  transition: opacity 0.2s;
}

.forgot-link:hover {
  text-decoration: underline;
}

.forgot-link:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
